import { z } from 'zod';

import { cuidLike, nonEmptyString } from '../zod-common';

export const DISPUTE_SCOPES = ['TASK', 'QUOTE_AMOUNT', 'SCHEDULE', 'BEHAVIOR', 'OTHER'] as const;
export type DisputeScopeValue = (typeof DISPUTE_SCOPES)[number];

export const DISPUTE_LEVELS = ['LEVEL_1', 'LEVEL_2', 'LEVEL_3'] as const;
export type DisputeLevelValue = (typeof DISPUTE_LEVELS)[number];

export const DISPUTE_STATUSES = [
  'OPEN',
  'OPEN_AUTORESOLVE',
  'PENDING_ADMIN_MEDIATION',
  'RESOLVED_AUTORESOLVE',
  'RESOLVED_LVL2',
  'RESOLVED_LVL3',
  'UNDER_REVIEW',
  'RESOLVED',
  'CLOSED',
] as const;
export type DisputeStatusValue = (typeof DISPUTE_STATUSES)[number];

export const SANCTION_TYPES = [
  'WARNING',
  'RATING_PENALTY',
  'TEMPORARY_BAN',
  'PERMANENT_BAN',
  'PLAN_DEMOTION',
  'COMMISSION_CREDIT_REVOKE',
] as const;
export type SanctionTypeValue = (typeof SANCTION_TYPES)[number];

export const ADMIN_MEDIATION_DECISIONS = [
  'ACCEPT_PROPOSAL',
  'REJECT_PROPOSAL',
  'CUSTOM_RESOLUTION',
] as const;
export type AdminMediationDecisionValue = (typeof ADMIN_MEDIATION_DECISIONS)[number];

const r2KeyLike = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .regex(/^[A-Za-z0-9._\-/]+$/, 'Invalid R2 key');

export const disputeEvidenceSchema = z
  .object({
    attachmentsR2Keys: z.array(r2KeyLike).max(20).optional(),
    referencedTaskIds: z.array(cuidLike).max(50).optional(),
  })
  .strict()
  .optional();
export type DisputeEvidenceInput = z.infer<typeof disputeEvidenceSchema>;

export const disputeFinancialAdjustmentSchema = z
  .object({
    direction: z.enum(['REQUESTER_TO_BIDDER', 'BIDDER_TO_REQUESTER']),
    amount: z.number().nonnegative().max(10_000_000),
    currency: z.enum(['EUR', 'USD']),
    note: z.string().trim().max(2_000).optional(),
  })
  .strict();
export type DisputeFinancialAdjustmentInput = z.infer<typeof disputeFinancialAdjustmentSchema>;

export const disputeOpenSchema = z.object({
  projectId: cuidLike,
  scope: z.enum(DISPUTE_SCOPES),
  summary: nonEmptyString.max(5_000, 'Max 5000 caractères'),
  evidence: disputeEvidenceSchema,
});
export type DisputeOpenInput = z.infer<typeof disputeOpenSchema>;

export const disputeByIdSchema = z.object({
  disputeId: cuidLike,
});
export type DisputeByIdInput = z.infer<typeof disputeByIdSchema>;

export const disputeByProjectSchema = z.object({
  projectId: cuidLike,
});
export type DisputeByProjectInput = z.infer<typeof disputeByProjectSchema>;

export const disputeProposeResolutionSchema = z.object({
  disputeId: cuidLike,
  proposal: nonEmptyString.max(5_000, 'Max 5000 caractères'),
  recommendedFinancialAdjustment: disputeFinancialAdjustmentSchema.optional(),
});
export type DisputeProposeResolutionInput = z.infer<typeof disputeProposeResolutionSchema>;

export const disputeAcceptResolutionSchema = z.object({
  disputeId: cuidLike,
});
export type DisputeAcceptResolutionInput = z.infer<typeof disputeAcceptResolutionSchema>;

export const disputeRejectResolutionSchema = z.object({
  disputeId: cuidLike,
  reason: nonEmptyString.max(2_000, 'Max 2000 caractères'),
});
export type DisputeRejectResolutionInput = z.infer<typeof disputeRejectResolutionSchema>;

export const disputeEscalateSchema = z.object({
  disputeId: cuidLike,
});
export type DisputeEscalateInput = z.infer<typeof disputeEscalateSchema>;

export const disputeAdminMediationSchema = z
  .object({
    disputeId: cuidLike,
    decision: z.enum(ADMIN_MEDIATION_DECISIONS),
    customResolution: z
      .object({
        summary: nonEmptyString.max(5_000),
        recommendedFinancialAdjustment: disputeFinancialAdjustmentSchema.optional(),
      })
      .strict()
      .optional(),
    comment: nonEmptyString.max(5_000, 'Max 5000 caractères'),
  })
  .refine((data) => data.decision !== 'CUSTOM_RESOLUTION' || data.customResolution !== undefined, {
    message: 'CUSTOM_RESOLUTION_REQUIRES_PAYLOAD',
    path: ['customResolution'],
  });
export type DisputeAdminMediationInput = z.infer<typeof disputeAdminMediationSchema>;

const sanctionEntrySchema = z
  .object({
    userId: cuidLike,
    sanctionType: z.enum(SANCTION_TYPES),
    durationDays: z.number().int().positive().max(3650).optional(),
    reason: nonEmptyString.max(2_000),
  })
  .strict()
  .refine((data) => data.sanctionType !== 'TEMPORARY_BAN' || data.durationDays !== undefined, {
    message: 'DURATION_REQUIRED_FOR_TEMPORARY_BAN',
    path: ['durationDays'],
  });
export type SanctionEntryInput = z.infer<typeof sanctionEntrySchema>;

export const disputeAdminSanctionSchema = z.object({
  disputeId: cuidLike,
  sanctions: z.array(sanctionEntrySchema).min(1).max(10),
  cancelProject: z.boolean().default(false),
  comment: nonEmptyString.max(5_000, 'Max 5000 caractères'),
});
export type DisputeAdminSanctionInput = z.infer<typeof disputeAdminSanctionSchema>;

export const disputeAdminListSchema = z
  .object({
    status: z.enum(DISPUTE_STATUSES).optional(),
    level: z.enum(DISPUTE_LEVELS).optional(),
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();
export type DisputeAdminListInput = z.infer<typeof disputeAdminListSchema>;
